import React from "react";
import { Empty, Spin } from "antd";
import { utcToLocalDateTime, utcToLocalHm } from "./rampChecklistTime";

const BORDER = "1px solid #333";
const cell = { border: BORDER, padding: "4px 8px", verticalAlign: "top" };
const labelCell = { ...cell, textAlign: "center", fontWeight: 600, background: "#fafafa", width: "20%" };
const valueCell = { ...cell, height: 26 };
const headCell = { ...cell, textAlign: "center", background: "#fafafa" };

/**
 * 过站检查单的"纸质表版式"只读视图（OCM SECTION D - Annex 1）。
 *
 * 桌面航班详情抽屉的标签页用它，尽量还原纸质《Ramp Transit Checklist》的表格样式：
 * 顶部抬头 + 表头两行网格（航班/到达信息、表头要填项/离港信息）+ Tasks/Time/Check 三列任务表
 * + 末尾 Remark / Signature 行。手机端录入仍用 ChecklistBody（卡片表单），这里只负责"看起来和纸一样"。
 */
export default function ChecklistSheet({ state }) {
    const { loading, notFound, checklist, flight, headerAttrs, items, tzId, completed } = state;

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: 48 }}>
                <Spin size="large" tip="加载检查单…">
                    <div style={{ height: 1 }} />
                </Spin>
            </div>
        );
    }
    if (notFound) {
        return <Empty description="找不到这班航段，请确认计划是否已发布" style={{ padding: 32 }} />;
    }
    if (!checklist?.initialized) {
        return <Empty description="尚未开始过站检查" style={{ padding: 32 }} />;
    }

    const hm = (utcIso) => (utcIso ? utcToLocalHm(utcIso, tzId) : "");
    const dt = (utcIso) => (utcIso ? utcToLocalDateTime(utcIso, tzId, "YYYY-MM-DD HH:mm") : "");
    const pair = (a, b) => [hm(a), hm(b)].filter(Boolean).join(" / ");

    return (
        <div style={{ background: "#fff", color: "#000", padding: 16, fontSize: 13, lineHeight: 1.5 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <strong>OCM SECTION D - ANNEX 1</strong>
                <strong style={{ color: "#1677ff" }}>Fly Meta</strong>
            </div>
            <div style={{ textAlign: "center", fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Ramp Transit Checklist</div>
            <div style={{ marginBottom: 8 }}>Station: {flight.dep || ""}</div>

            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12, tableLayout: "fixed" }}>
                <tbody>
                    <tr>
                        <td style={labelCell}>Flight Number</td>
                        <td style={labelCell}>Aircraft REG</td>
                        <td style={labelCell}>Date</td>
                        <td style={labelCell}>STA/ETA</td>
                        <td style={labelCell}>ATA</td>
                    </tr>
                    <tr>
                        <td style={valueCell}>{flight.flight_no || ""}</td>
                        <td style={valueCell}>{flight.aircraft_reg || ""}</td>
                        <td style={valueCell}>{flight.flight_date || ""}</td>
                        <td style={valueCell}>{pair(flight.sta, flight.eta)}</td>
                        <td style={valueCell}>{hm(headerAttrs.ata || flight.ata)}</td>
                    </tr>
                    <tr>
                        <td style={labelCell}>RCO</td>
                        <td style={labelCell}>Parking Spot</td>
                        <td style={labelCell}>Crew Figure</td>
                        <td style={labelCell}>STD/ETD</td>
                        <td style={labelCell}>ATD</td>
                    </tr>
                    <tr>
                        <td style={valueCell}>{headerAttrs.rco || ""}</td>
                        <td style={valueCell}>{headerAttrs.parking_spot || ""}</td>
                        <td style={valueCell}>{headerAttrs.crew_figure ?? ""}</td>
                        <td style={valueCell}>{pair(flight.std, flight.etd)}</td>
                        <td style={valueCell}>{hm(headerAttrs.atd || flight.atd)}</td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={headCell}>Tasks</th>
                        <th style={{ ...headCell, width: 104 }}>Time</th>
                        <th style={{ ...headCell, width: 70 }}>Check (√)</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((record) => {
                        const a = record.attributes || {};
                        return (
                            <tr key={record.item_id}>
                                <td style={cell}>{a.label_en}</td>
                                <td style={{ ...cell, textAlign: "center" }}>{a.time ? utcToLocalDateTime(a.time, tzId, "MM-DD HH:mm") : ""}</td>
                                <td style={{ ...cell, textAlign: "center", fontWeight: 700, color: "#237804" }}>{a.checked ? "√" : ""}</td>
                            </tr>
                        );
                    })}
                    <tr>
                        <td style={cell}>
                            <strong>Remark:</strong> {headerAttrs.remark || ""}
                        </td>
                        <td style={cell} colSpan={2}>
                            <strong>Signature:</strong>
                            <div style={{ marginTop: 4 }}>
                                {headerAttrs.signature_data ? (
                                    <img src={headerAttrs.signature_data} alt="签名" style={{ maxHeight: 56, maxWidth: "100%", border: "1px solid #eee", background: "#fff" }} />
                                ) : (
                                    <span style={{ color: "#999" }}>未签名</span>
                                )}
                            </div>
                            <div style={{ color: "#666", marginTop: 4 }}>
                                {headerAttrs.signed_by || ""}
                                {headerAttrs.signed_at ? ` · ${dt(headerAttrs.signed_at)}` : ""}
                            </div>
                            {completed ? <div style={{ color: "#237804", marginTop: 2 }}>已完成{headerAttrs.completed_at ? ` · ${dt(headerAttrs.completed_at)}` : ""}</div> : null}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
